/**
 * Meal plan detail provider + helpers.
 *
 * Owns cross-platform data access, mutations, and shared meal/slot view-model helpers used by both
 * web and mobile.
 */
import {
  createContext,
  useCallback,
  useContext as useReactContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useQuery, useSuspenseQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, PromiseWithServerResult } from "@rocicorp/zero";
import type { FoodItemMatcher } from "@corpuscomplete/core/nutrition/food-matcher";
import { Food } from "@corpuscomplete/core/nutrition/food";
import { MealPlan } from "@corpuscomplete/core/nutrition/meal-plan";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import type { NutritionProgram } from "@corpuscomplete/core/nutrition/program";
import { AgentSession } from "@corpuscomplete/core/ai/agent-session";
import { WorkflowExecution } from "@corpuscomplete/core/ai/workflow";
import { MealPlanAgent } from "@corpuscomplete/core/nutrition/meal-plan-agent";
import { mutators, queries } from "@corpuscomplete/core/zero";
import {
  createId,
  must,
  NIL_UUID,
  nSortIndexesBetween,
  sort,
  sortIndexBetween,
} from "@corpuscomplete/core/utils";
import { AgentTools } from "../../../ai/agent/tools";
import { AskQuestions } from "../../../ai/agent/tools";
import { DraftUI } from "../../../common/draft";
import { DateRange } from "@corpuscomplete/core/common/date-time";

export namespace MealPlanDetail {
  type PlanningPart = MealPlanAgent.PlanningUIMessage["parts"][number];

  export type ProposePlanPart = Extract<PlanningPart, { type: "tool-proposePlan" }>;
  export type AskQuestionsPart = Extract<PlanningPart, { type: "tool-askQuestions" }>;

  export type ProposePlanCompletedPart = Extract<ProposePlanPart, { state: "output-available" }>;
  export type AskQuestionsCompletedPart = Extract<AskQuestionsPart, { state: "output-available" }>;

  export function isProposePlanPart(part: { type: string }): part is ProposePlanPart {
    return part.type === "tool-proposePlan";
  }

  export function isAskQuestionsPart(part: { type: string }): part is AskQuestionsPart {
    return AskQuestions.isPart(part);
  }

  export function isProposePlanCompletedPart(part: {
    type: string;
    state?: string;
  }): part is ProposePlanCompletedPart {
    return part.type === "tool-proposePlan" && part.state === "output-available";
  }

  export function isAskQuestionsCompletedPart(part: {
    type: string;
    state?: string;
  }): part is AskQuestionsCompletedPart {
    return AskQuestions.isCompletedPart(part);
  }

  export type MealWorkflowExecutionLike = {
    workflowExecutionId: string;
    status: WorkflowExecution.Status;
    timeCreated: Temporal.Instant;
  };

  export type MealWorkflowProgressSummary = {
    totalMeals: number;
    completedMeals: number;
    progressPercent: number;
    hasActiveWork: boolean;
  };

  export type MergedItem =
    | {
        id: string;
        sortIndex: string;
        type: "item";
        item: Food.Item;
      }
    | {
        id: string;
        sortIndex: string;
        type: "descriptor";
        descriptor: FoodItemMatcher.ItemDescriptor;
      };

  export type MergedMeal = Omit<
    MealPlan.Meal,
    "items" | "itemDescriptors" | "timeUpdated" | "timeArchived"
  > & {
    occurrenceCount: number;
    items: readonly MergedItem[];
    hasDescriptors: boolean;
    workflowStatus: string | undefined;
  };

  export type UserNutrientTarget = {
    userId: string;
    nutrientTargets: Nutrient.Targets | null;
  };

  export type SlotNutrientItem =
    | {
        type: "item";
        item: Food.Item;
      }
    | {
        type: "descriptor";
      };

  export type SlotNutrientMealLike = {
    hasDescriptors: boolean;
    items: readonly SlotNutrientItem[];
  };

  export type SortableByIndex = {
    sortIndex: string;
  };

  export type MealItemDropTarget = {
    prevId: string | null;
    nextId: string | null;
  };

  export type ReorderMealItemArgs = {
    activeId: string;
    dropTarget: MealItemDropTarget | null;
  };

  type ReorderMealItemPlanArgs = ReorderMealItemArgs & {
    items: readonly MergedItem[];
  };

  type SingleItemReorder = {
    type: "single";
    item: MergedItem;
    sortIndex: string;
  };

  type RepairMealItemReorder = {
    type: "repair";
    itemEdits: Pick<MealPlan.MealItem, "id" | "sortIndex">[];
    descriptorEdits: Pick<MealPlan.MealItemDescriptor, "id" | "sortIndex">[];
  };

  export type ChatUnavailabilityWarningContent = {
    message: string;
    showProgramsLink: boolean;
    programsLinkLabel: string;
    programsLinkSuffix: string;
  };

  const chatUnavailabilityMessagesByReason: Record<
    MealPlanAgent.UnavailableReason,
    ChatUnavailabilityWarningContent
  > = {
    no_program: {
      message: "No nutrition program.",
      showProgramsLink: true,
      programsLinkLabel: "Create one",
      programsLinkSuffix: " for personalized targets.",
    },
    no_program_for_dates: {
      message: "Program doesn't cover these dates.",
      showProgramsLink: false,
      programsLinkLabel: "Create one",
      programsLinkSuffix: " for personalized targets.",
    },
    inconsistent_days: {
      message: "Varied-day programs not yet supported.",
      showProgramsLink: false,
      programsLinkLabel: "Create one",
      programsLinkSuffix: " for personalized targets.",
    },
  };

  export function mergeMealsAndWorkflowStatusByMealId(
    meals: readonly MealPlan.Meal[],
    byMealId: Record<string, readonly MealWorkflowExecutionLike[]>,
  ): MergedMeal[] {
    return meals.map((meal) => {
      const latest = getLatestWorkflowExecution(byMealId[meal.id]);
      return mergeMeal(meal, latest?.status);
    });
  }

  function deriveMealWorkflowProgressSummary(args: {
    meals: readonly Pick<MealPlan.Meal, "id">[];
    byMealId: Record<string, readonly MealWorkflowExecutionLike[]>;
  }): MealWorkflowProgressSummary | null {
    const { meals, byMealId } = args;

    if (meals.length === 0) {
      return null;
    }

    let hasAnyWorkflow = false;
    let completedMeals = 0;
    let hasActiveWork = false;

    for (const meal of meals) {
      const latestExecution = getLatestWorkflowExecution(byMealId[meal.id]);
      if (!latestExecution) {
        continue;
      }

      hasAnyWorkflow = true;

      if (WorkflowExecution.isTerminalStatus(latestExecution.status)) {
        completedMeals += 1;
      }

      if (WorkflowExecution.isActiveStatus(latestExecution.status)) {
        hasActiveWork = true;
      }
    }

    if (!hasAnyWorkflow) {
      return null;
    }

    const totalMeals = meals.length;

    return {
      totalMeals,
      completedMeals,
      progressPercent: Math.round((completedMeals / totalMeals) * 100),
      hasActiveWork,
    };
  }

  function getLatestWorkflowExecution(
    executions: readonly MealWorkflowExecutionLike[] | undefined,
  ): MealWorkflowExecutionLike | undefined {
    if (!executions || executions.length === 0) {
      return undefined;
    }

    return executions.reduce((latest, candidate) => {
      if (!latest) {
        return candidate;
      }

      if (Temporal.Instant.compare(candidate.timeCreated, latest.timeCreated) > 0) {
        return candidate;
      }

      if (Temporal.Instant.compare(candidate.timeCreated, latest.timeCreated) === 0) {
        return candidate.workflowExecutionId > latest.workflowExecutionId ? candidate : latest;
      }

      return latest;
    }, executions[0]);
  }

  export function buildSlotHeaderNutrients(options: {
    userId: string;
    userNutrientTargets: readonly UserNutrientTarget[];
    mergedMeals: readonly SlotNutrientMealLike[];
  }): {
    slotTargets: Nutrient.Targets | null;
    slotFoodItems: Food.Item[];
    slotHasDescriptors: boolean;
    hasSlotActuals: boolean;
  } {
    const { userId, userNutrientTargets, mergedMeals } = options;

    const slotTargets =
      userNutrientTargets.find((target) => target.userId === userId)?.nutrientTargets ?? null;

    const slotFoodItems: Food.Item[] = [];
    let slotHasDescriptors = false;

    for (const meal of mergedMeals) {
      slotHasDescriptors = slotHasDescriptors || meal.hasDescriptors;
      for (const item of meal.items) {
        if (item.type === "item") {
          slotFoodItems.push(item.item);
        }
      }
    }

    const hasSlotActuals = slotFoodItems.length > 0 || slotHasDescriptors;

    return {
      slotTargets,
      slotFoodItems,
      slotHasDescriptors,
      hasSlotActuals,
    };
  }

  export function getChatUnavailabilityWarningContent(
    reason: MealPlanAgent.UnavailableReason | null,
  ): ChatUnavailabilityWarningContent | null {
    if (!reason) {
      return null;
    }

    return chatUnavailabilityMessagesByReason[reason];
  }

  export function getNextMealSortIndexInLane(args: {
    meals: readonly Pick<MealPlan.Meal, "mealSlotId" | "userId" | "sortIndex">[];
    mealSlotId: string;
    userId: string;
  }): string {
    const laneMeals = sort(
      args.meals.filter(
        (meal) => meal.mealSlotId === args.mealSlotId && meal.userId === args.userId,
      ),
    );
    const lastSortIndex = laneMeals.length > 0 ? laneMeals[laneMeals.length - 1]?.sortIndex : null;
    return sortIndexBetween(lastSortIndex, null);
  }

  export function getNextMealItemSortIndex(items: readonly SortableByIndex[]): string {
    const sortedItems = sort(items);
    const lastSortIndex =
      sortedItems.length > 0 ? sortedItems[sortedItems.length - 1]?.sortIndex : null;
    return sortIndexBetween(lastSortIndex, null);
  }

  function mergeMeal(meal: MealPlan.Meal, workflowStatus: string | undefined): MergedMeal {
    const { items, hasDescriptors } = mergeItemsWithDescriptors(meal.items, meal.itemDescriptors);

    return {
      ...meal,
      occurrenceCount: meal.occurrences.length,
      items,
      hasDescriptors,
      workflowStatus,
    };
  }

  function mergeItemsWithDescriptors(
    items: MealPlan.Meal["items"],
    descriptors: MealPlan.Meal["itemDescriptors"],
  ): {
    items: readonly MergedItem[];
    hasDescriptors: boolean;
  } {
    const result: MergedItem[] = [];

    for (const item of items) {
      result.push({
        id: item.id,
        sortIndex: item.sortIndex,
        type: "item",
        item,
      });
    }

    let hasDescriptors = false;
    for (const descriptor of descriptors) {
      result.push({
        id: descriptor.id,
        sortIndex: descriptor.sortIndex,
        type: "descriptor",
        descriptor: descriptor.data,
      });
      hasDescriptors = true;
    }

    return {
      items: sort(result),
      hasDescriptors,
    };
  }

  const deferredToolLabels = {
    "tool-proposePlan": "Review plan",
    "tool-askQuestions": "Questions",
  } as const;

  type DeferredToolType = keyof typeof deferredToolLabels;

  function isDeferredToolPart(part: AgentTools.DeferredTool): boolean {
    return part.type in deferredToolLabels;
  }

  function getDeferredToolLabel(part: AgentTools.DeferredTool | undefined): string | undefined {
    if (!part || !(part.type in deferredToolLabels)) {
      return undefined;
    }

    return deferredToolLabels[part.type as DeferredToolType];
  }

  export const deferredToolPresenter: AgentTools.DeferredToolPresenter = {
    isDeferredToolPart,
    getDeferredToolLabel,
  };

  export type Context = {
    mealPlanId: string;
    userId: string;
    showPublishErrors: boolean;
    setShowPublishErrors: (next: boolean) => void;
  };

  const Context = createContext<Context | null>(null);

  export function Provider({
    children,
    mealPlanId,
    userId,
  }: PropsWithChildren<{
    mealPlanId: string;
    userId: string;
  }>) {
    const [showPublishErrors, setShowPublishErrors] = useState(false);
    const [rawMealPlan] = useQuery(queries.mealPlan.byId({ id: mealPlanId }));

    const mealPlan = rawMealPlan ? MealPlan.asInfo(rawMealPlan) : undefined;
    const publishViolations = useMemo(
      () => (mealPlan ? MealPlan.publishViolations(mealPlan) : null),
      [mealPlan],
    );

    useEffect(() => {
      if (mealPlan?.status === "published" || (showPublishErrors && publishViolations === null)) {
        setShowPublishErrors(false);
      }
    }, [mealPlan?.status, publishViolations, showPublishErrors]);

    const value = useMemo(
      () => ({ mealPlanId, userId, showPublishErrors, setShowPublishErrors }),
      [mealPlanId, showPublishErrors, userId],
    );

    return <Context value={value}>{children}</Context>;
  }

  function useContext(): Context {
    const context = useReactContext(Context);
    if (!context) {
      throw new Error("MealPlanDetail context hooks must be used within MealPlanDetail.Provider");
    }
    return context;
  }

  export function useMealPlan(): MealPlan.Info | undefined {
    const { mealPlanId } = useContext();
    const [mealPlan] = useQuery(queries.mealPlan.byId({ id: mealPlanId }));
    return mealPlan ? MealPlan.asInfo(mealPlan) : undefined;
  }

  export type AgentSessionInfo = AgentSession.Info<typeof MealPlanAgent.definition>;

  export function useAgentSession(): {
    session: AgentSessionInfo | undefined;
  } {
    const { mealPlanId } = useContext();
    const [rawSession] = useQuery(queries.mealPlanAgentSession.byMealPlanId({ mealPlanId }));

    const session =
      rawSession?.base && AgentSession.parse(MealPlanAgent.definition, rawSession.base);

    return {
      session: session ?? undefined,
    };
  }

  export function useAgentSessionSuspense(): {
    session: AgentSessionInfo | undefined;
  } {
    const { mealPlanId } = useContext();
    const [rawSession] = useSuspenseQuery(
      queries.mealPlanAgentSession.byMealPlanId({ mealPlanId }),
      {
        suspendUntil: "complete",
      },
    );

    const session =
      rawSession?.base && AgentSession.parse(MealPlanAgent.definition, rawSession.base);

    return {
      session: session ?? undefined,
    };
  }

  export function useMealWorkflowProgressSummary(): MealWorkflowProgressSummary | null {
    const mealPlan = useMealPlan();
    const { session } = useAgentSession();
    const { byMealId } = useMealWorkflowExecutionsByMeal({
      agentSessionId: session?.id ?? NIL_UUID,
    });

    return useMemo(
      () =>
        deriveMealWorkflowProgressSummary({
          meals: mealPlan ? MealPlan.allMeals(mealPlan) : [],
          byMealId,
        }),
      [mealPlan, byMealId],
    );
  }

  export function useProposePlanDraftForm({
    toolCallId,
    input,
    nutrientTargetsMode,
    onSubmit,
  }: {
    toolCallId: string;
    input: unknown;
    nutrientTargetsMode: MealPlanAgent.PlanFormNutrientTargetsMode;
    onSubmit: (props: { value: MealPlanAgent.PlanForm }) => Promise<void> | void;
  }) {
    const originalDraft = useMemo(
      () => MealPlanAgent.PlanDraftSchema.safeParse(input).data ?? { slots: [] },
      [input],
    );

    const result = DraftUI.useDraftForm({
      type: "agentTools.proposePlan",
      draftKey: toolCallId,
      schemas: {
        storage: MealPlanAgent.planFormSchema("storage", nutrientTargetsMode),
        validation: MealPlanAgent.planFormSchema("validation", nutrientTargetsMode),
      },
      defaultValue: () => MealPlanAgent.planDraftToForm(originalDraft, nutrientTargetsMode),
      onSubmit,
    });

    const reset = useCallback(() => {
      result.resetTo(MealPlanAgent.planDraftToForm(originalDraft, nutrientTargetsMode));
    }, [nutrientTargetsMode, originalDraft, result]);

    return useMemo(
      () => ({ ...result, reset, originalDraft, nutrientTargetsMode }),
      [nutrientTargetsMode, originalDraft, reset, result],
    );
  }

  export function useMealWorkflowExecutionsByMeal(args: { agentSessionId: string }): {
    byMealId: Record<string, MealWorkflowExecutionLike[]>;
    isLoading: boolean;
  } {
    const querySessionId = args.agentSessionId || NIL_UUID;

    const [sessionWorkflows, details] = useQuery(
      queries.mealPlanAgentSession.mealWorkflowsBySessionId({ sessionId: querySessionId }),
      { enabled: Boolean(args.agentSessionId) },
    );

    const byMealId = useMemo(() => {
      if (!sessionWorkflows?.mealWorkflowExecutions) {
        return {};
      }

      const grouped: Record<string, MealWorkflowExecutionLike[]> = {};

      for (const link of sessionWorkflows.mealWorkflowExecutions) {
        if (!link.workflowExecution) {
          continue;
        }

        const workflowExecutionId = link.workflowExecutionId;
        const status = link.workflowExecution.status;

        let byMeal = grouped[link.mealId];
        if (!byMeal) {
          byMeal = [];
          grouped[link.mealId] = byMeal;
        }

        byMeal.push({
          workflowExecutionId,
          status,
          timeCreated: link.workflowExecution.timeCreated,
        });
      }

      for (const entries of Object.values(grouped)) {
        entries.sort((a, b) => {
          if (Temporal.Instant.compare(a.timeCreated, b.timeCreated) === 0) {
            return a.workflowExecutionId.localeCompare(b.workflowExecutionId);
          }
          return Temporal.Instant.compare(a.timeCreated, b.timeCreated) < 0 ? 1 : -1;
        });
      }

      return grouped;
    }, [sessionWorkflows]);

    return {
      byMealId,
      isLoading: details.type === "unknown",
    };
  }

  export type DomainConstraints = {
    canEdit: boolean;
    canDelete: boolean;
    canPublish: boolean;
    canUnpublish: boolean;
    canCreateAgentSession: boolean;
  };

  export type PublishViolationTarget = MealPlan.PublishViolationTarget;

  export function usePublishViolations() {
    const mealPlan = useMealPlan();
    const { showPublishErrors } = useContext();

    return useMemo(
      () => (showPublishErrors && mealPlan ? MealPlan.publishViolations(mealPlan) : null),
      [mealPlan, showPublishErrors],
    );
  }

  export function useSlotPublishViolations(slotId: string) {
    const violations = usePublishViolations();
    const slotViolations = violations?.slots[slotId];

    return useMemo(
      () => ({
        emptyName: slotViolations?.emptyName === true,
        noMeals: slotViolations?.noMeals === true,
      }),
      [slotViolations],
    );
  }

  export function useMealPublishViolations(mealId: string) {
    const violations = usePublishViolations();
    const mealViolations = violations?.meals[mealId];

    return useMemo(
      () => ({
        emptyName: mealViolations?.emptyName === true,
        noItems: mealViolations?.noItems === true,
        hasDescriptors: mealViolations?.hasDescriptors === true,
      }),
      [mealViolations],
    );
  }

  export function useDomainConstraints(): DomainConstraints {
    const { userId } = useContext();
    const mealPlan = useMealPlan();
    const { session } = useAgentSession();

    return useMemo(() => {
      if (!mealPlan) {
        return {
          canEdit: false,
          canDelete: false,
          canPublish: false,
          canUnpublish: false,
          canCreateAgentSession: false,
        };
      }

      const isOwner = mealPlan.ownerId === userId;
      const isDraft = mealPlan.status === "draft";
      const isPublished = mealPlan.status === "published";
      const hasSession = !!session;

      return {
        canEdit: isOwner && isDraft,
        canDelete: isOwner && isDraft,
        canPublish: isOwner && isDraft,
        canUnpublish: isOwner && isPublished,
        canCreateAgentSession: isOwner && isDraft && !hasSession,
      };
    }, [mealPlan, userId, session]);
  }

  export type Mutations = {
    publish: (options?: {
      onError?: (target: MealPlan.PublishViolationTarget) => void;
    }) => PromiseWithServerResult | undefined;
    unpublish: () => PromiseWithServerResult;
    deleteMealPlan: () => PromiseWithServerResult;
    createAgentSession: () => PromiseWithServerResult;
    addMealSlot: (slot: Omit<MealPlan.AddMealSlot, "mealPlanId">) => PromiseWithServerResult;
    removeMealSlot: (slotId: string) => PromiseWithServerResult;
    editMealSlot: (
      slotId: string,
      updates: Partial<Pick<MealPlan.Slot, "name" | "time">>,
    ) => PromiseWithServerResult;
    setMealUserNutrientTargets: (args: {
      userId: string;
      mealSlotId: string;
      nutrientTargets: Nutrient.Targets;
    }) => PromiseWithServerResult;
    removeMealUserNutrientTargets: (args: {
      userId: string;
      mealSlotId: string;
    }) => PromiseWithServerResult;
    addMeal: (meal: Omit<MealPlan.AddMeal, "mealPlanId">) => PromiseWithServerResult;
    removeMeal: (mealId: string) => PromiseWithServerResult;
    editMeal: (
      mealId: string,
      updates: Partial<Pick<MealPlan.Meal, "name" | "sortIndex">>,
    ) => PromiseWithServerResult;
    addMealItem: (
      mealId: string,
      item: Pick<MealPlan.MealItem, "id" | "foodServingId" | "amount" | "sortIndex">,
    ) => PromiseWithServerResult;
    editMealItem: (
      mealId: string,
      itemId: string,
      updates: Partial<Pick<MealPlan.MealItem, "amount" | "foodServingId" | "sortIndex">>,
    ) => PromiseWithServerResult;
    reorderMealItem: (
      mealId: string,
      args: ReorderMealItemArgs,
    ) => PromiseWithServerResult | undefined;
    removeMealItem: (mealId: string, itemId: string) => PromiseWithServerResult;
    removeMealItemDescriptor: (mealId: string, descriptorId: string) => PromiseWithServerResult;
    addMealOccurrence: (
      mealId: string,
      occurrence: {
        id: string;
        date: Temporal.PlainDate | null;
      },
    ) => PromiseWithServerResult;
    editMealOccurrence: (
      mealId: string,
      occurrenceId: string,
      updates: Partial<{
        date: Temporal.PlainDate | null;
      }>,
    ) => PromiseWithServerResult;
    removeMealOccurrence: (mealId: string, occurrenceId: string) => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();
    const { mealPlanId, setShowPublishErrors } = useContext();
    const mealPlan = useMealPlan();

    return useMemo(
      () => ({
        publish: (options) => {
          if (!mealPlan) {
            return undefined;
          }

          const violations = MealPlan.publishViolations(mealPlan);
          if (violations) {
            setShowPublishErrors(true);
            options?.onError?.(violations.firstTarget);
            return undefined;
          }

          return z.mutate(mutators.mealPlan.publish({ id: mealPlanId }));
        },
        unpublish: () => z.mutate(mutators.mealPlan.unpublish({ id: mealPlanId })),
        deleteMealPlan: () => z.mutate(mutators.mealPlan.delete({ id: mealPlanId })),
        createAgentSession: () =>
          z.mutate(
            mutators.agentSession.create(
              AgentSession.createArgs(MealPlanAgent.definition, {
                params: {
                  mealPlanId,
                  userId: must(mealPlan?.ownerId, "Meal plan owner id is required"),
                  initialMessageId: createId(),
                },
              }),
            ),
          ),
        addMealSlot: (slot) =>
          z.mutate(
            mutators.mealPlan.addMealSlot({
              ...slot,
              mealPlanId,
              time: Temporal.PlainTime.from(slot.time),
            }),
          ),
        removeMealSlot: (slotId) =>
          z.mutate(mutators.mealPlan.removeMealSlot({ id: slotId, mealPlanId })),
        editMealSlot: (slotId, updates) =>
          (() => {
            const { time, ...rest } = updates;
            return z.mutate(
              mutators.mealPlan.editMealSlot({
                id: slotId,
                mealPlanId,
                ...rest,
                ...(time ? { time: Temporal.PlainTime.from(time) } : {}),
              }),
            );
          })(),
        setMealUserNutrientTargets: (args) =>
          z.mutate(mutators.mealPlan.setMealUserNutrientTargets({ ...args, mealPlanId })),
        removeMealUserNutrientTargets: (args) =>
          z.mutate(mutators.mealPlan.removeMealUserNutrientTargets({ ...args, mealPlanId })),
        addMeal: (meal) => z.mutate(mutators.mealPlan.addMeal({ ...meal, mealPlanId })),
        removeMeal: (mealId) => z.mutate(mutators.mealPlan.removeMeal({ id: mealId, mealPlanId })),
        editMeal: (mealId, updates) =>
          z.mutate(mutators.mealPlan.editMeal({ id: mealId, mealPlanId, ...updates })),
        addMealItem: (mealId, item) =>
          z.mutate(mutators.mealPlan.addMealItem({ ...item, mealId, mealPlanId })),
        editMealItem: (mealId, itemId, updates) =>
          z.mutate(mutators.mealPlan.editMealItem({ id: itemId, mealId, mealPlanId, ...updates })),
        reorderMealItem: (mealId, args) => {
          const items = findMergedMealItemsById(mealPlan, mealId);
          if (!items) {
            return undefined;
          }

          const reorder = planMealItemReorder({ ...args, items });
          if (!reorder) {
            return undefined;
          }

          if (reorder.type === "single") {
            if (reorder.item.type === "item") {
              return z.mutate(
                mutators.mealPlan.editMealItem({
                  id: reorder.item.id,
                  mealId,
                  mealPlanId,
                  sortIndex: reorder.sortIndex,
                }),
              );
            }

            return z.mutate(
              mutators.mealPlan.editMealItemDescriptor({
                id: reorder.item.id,
                mealId,
                mealPlanId,
                sortIndex: reorder.sortIndex,
              }),
            );
          }

          if (reorder.itemEdits.length === 0 && reorder.descriptorEdits.length === 0) {
            return undefined;
          }

          return z.mutate(
            mutators.mealPlan.editMeal({
              id: mealId,
              mealPlanId,
              ...(reorder.itemEdits.length > 0 ? { items: { edit: reorder.itemEdits } } : {}),
              ...(reorder.descriptorEdits.length > 0
                ? { itemDescriptors: { edit: reorder.descriptorEdits } }
                : {}),
            }),
          );
        },
        removeMealItem: (mealId, itemId) =>
          z.mutate(mutators.mealPlan.removeMealItem({ id: itemId, mealId, mealPlanId })),
        removeMealItemDescriptor: (mealId, descriptorId) =>
          z.mutate(
            mutators.mealPlan.removeMealItemDescriptor({ id: descriptorId, mealId, mealPlanId }),
          ),
        addMealOccurrence: (mealId, occurrence) =>
          z.mutate(mutators.mealPlan.addMealOccurrence({ ...occurrence, mealId, mealPlanId })),
        editMealOccurrence: (mealId, occurrenceId, updates) =>
          z.mutate(
            mutators.mealPlan.editMealOccurrence({
              id: occurrenceId,
              mealId,
              mealPlanId,
              ...updates,
            }),
          ),
        removeMealOccurrence: (mealId, occurrenceId) =>
          z.mutate(
            mutators.mealPlan.removeMealOccurrence({ id: occurrenceId, mealId, mealPlanId }),
          ),
      }),
      [z, mealPlan, mealPlanId, setShowPublishErrors],
    );
  }

  function findMergedMealItemsById(
    mealPlan: MealPlan.Info | null | undefined,
    mealId: string,
  ): readonly MergedItem[] | undefined {
    const meal = mealPlan?.slots.flatMap((slot) => slot.meals).find((item) => item.id === mealId);
    if (!meal) {
      return undefined;
    }

    return mergeItemsWithDescriptors(meal.items, meal.itemDescriptors).items;
  }

  function planMealItemReorder(
    args: ReorderMealItemPlanArgs,
  ): SingleItemReorder | RepairMealItemReorder | null {
    const reorderedItems = getReorderedMealItems(args);
    if (!reorderedItems) {
      return null;
    }

    const singleItemReorder = getSingleMealItemReorder(reorderedItems, args.activeId);
    if (singleItemReorder) {
      return singleItemReorder;
    }

    return getRepairMealItemReorder(reorderedItems);
  }

  export function getReorderedMealItems({
    items,
    activeId,
    dropTarget,
  }: ReorderMealItemPlanArgs): readonly MergedItem[] | null {
    if (!dropTarget) {
      return null;
    }

    const activeItem = items.find((item) => item.id === activeId);
    if (!activeItem) {
      return null;
    }

    const remainingItems = items.filter((item) => item.id !== activeId);
    const nextIndex =
      dropTarget.nextId === null
        ? remainingItems.length
        : remainingItems.findIndex((item) => item.id === dropTarget.nextId);
    if (nextIndex === -1) {
      return null;
    }

    const reorderedItems = [
      ...remainingItems.slice(0, nextIndex),
      activeItem,
      ...remainingItems.slice(nextIndex),
    ];

    return items.every((item, index) => item.id === reorderedItems[index]?.id)
      ? null
      : reorderedItems;
  }

  function getSingleMealItemReorder(
    reorderedItems: readonly MergedItem[],
    activeId: string,
  ): SingleItemReorder | null {
    const nextIndex = reorderedItems.findIndex((item) => item.id === activeId);
    const item = reorderedItems[nextIndex];
    if (!item) {
      return null;
    }

    const prev = reorderedItems[nextIndex - 1];
    const next = reorderedItems[nextIndex + 1];
    if (prev?.sortIndex && next?.sortIndex && prev.sortIndex === next.sortIndex) {
      return null;
    }

    const sortIndex = sortIndexBetween(prev?.sortIndex ?? null, next?.sortIndex ?? null);
    if (item.sortIndex === sortIndex) {
      return null;
    }

    return {
      type: "single",
      item,
      sortIndex,
    };
  }

  function getRepairMealItemReorder(reorderedItems: readonly MergedItem[]): RepairMealItemReorder {
    const nextSortIndexes = nSortIndexesBetween(null, null, reorderedItems.length);
    const itemEdits: RepairMealItemReorder["itemEdits"] = [];
    const descriptorEdits: RepairMealItemReorder["descriptorEdits"] = [];

    reorderedItems.forEach((item, index) => {
      const sortIndex = must(nextSortIndexes[index]);
      if (item.sortIndex === sortIndex) {
        return;
      }

      if (item.type === "item") {
        itemEdits.push({ id: item.id, sortIndex });
        return;
      }

      descriptorEdits.push({ id: item.id, sortIndex });
    });

    return {
      type: "repair",
      itemEdits,
      descriptorEdits,
    };
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { mealPlanId } = useContext();
    const [, mealPlanDetails] = useQuery(queries.mealPlan.byId({ id: mealPlanId }));
    const [, sessionDetails] = useQuery(queries.mealPlanAgentSession.byMealPlanId({ mealPlanId }));
    return {
      isLoading: mealPlanDetails.type === "unknown" || sessionDetails.type === "unknown",
    };
  }

  export type NutritionContextResult = {
    nutritionContext: MealPlanAgent.NutritionContext | null;
    unavailabilityReason: MealPlanAgent.UnavailableReason | null;
    isLoading: boolean;
    state: "loading" | "not_ready" | "ready";
  };

  function getMealPlanDateRange(mealPlan: MealPlan.Info | undefined): DateRange.Closed | null {
    if (!mealPlan) {
      return null;
    }
    return {
      dateStart: mealPlan.dateStart,
      dateEnd: mealPlan.dateEnd,
    };
  }

  function deriveNutritionContextResult(args: {
    mealPlan: MealPlan.Info | undefined;
    programs: NutritionProgram.Preview[];
    programInfo: { expenditure?: { goalOffset: number } | null } | null;
    dateRange: DateRange.Closed | null;
    isProgramsLoading: boolean;
    isProgramInfoLoading: boolean;
  }): NutritionContextResult {
    if (!args.mealPlan) {
      return {
        nutritionContext: null,
        unavailabilityReason: null,
        isLoading: false,
        state: "not_ready",
      };
    }

    if (args.isProgramsLoading || args.isProgramInfoLoading || !args.dateRange) {
      return {
        nutritionContext: null,
        unavailabilityReason: null,
        isLoading: true,
        state: "loading",
      };
    }

    const unavailabilityReason = MealPlanAgent.getUnavailabilityReason({
      programs: args.programs,
      dateStart: args.dateRange.dateStart.toString(),
      dateEnd: args.dateRange.dateEnd.toString(),
    });

    const nutritionContext = MealPlanAgent.computeNutritionContext({
      programs: args.programs,
      programInfo: args.programInfo,
      dateStart: args.dateRange.dateStart.toString(),
      dateEnd: args.dateRange.dateEnd.toString(),
    });

    return {
      nutritionContext,
      unavailabilityReason,
      isLoading: false,
      state: "ready",
    };
  }

  export function useNutritionContext(): NutritionContextResult {
    const mealPlan = useMealPlan();
    const dateRange = getMealPlanDateRange(mealPlan);
    const fallbackDate = Temporal.Now.plainDateISO("UTC");
    const ownerId = mealPlan?.ownerId ?? NIL_UUID;

    const [programs, programDetails] = useQuery(
      queries.nutritionProgram.inDateRange({
        userId: ownerId,
        dateRange: dateRange
          ? { dateStart: dateRange.dateStart, dateEnd: dateRange.dateEnd }
          : { dateStart: fallbackDate, dateEnd: fallbackDate },
      }),
      { enabled: dateRange !== null },
    );

    const [programInfo, programInfoDetails] = useQuery(
      queries.nutritionProgram.onDate({
        userId: ownerId,
        date: dateRange?.dateStart.toString() ?? fallbackDate.toString(),
      }),
      { enabled: dateRange !== null },
    );

    return useMemo(
      () =>
        deriveNutritionContextResult({
          mealPlan,
          programs,
          programInfo: programInfo ?? null,
          dateRange,
          isProgramsLoading: programDetails.type === "unknown",
          isProgramInfoLoading: programInfoDetails.type === "unknown",
        }),
      [mealPlan, programs, programInfo, dateRange, programDetails.type, programInfoDetails.type],
    );
  }

  export function preload(zero: Zero, mealPlanId: string): void {
    void zero.run(queries.mealPlan.byId({ id: mealPlanId }));
    void zero.run(queries.mealPlanAgentSession.byMealPlanId({ mealPlanId }));
  }
}
